import type { CardFace, CardLayout, ImageUris } from '@/types/card';

export type ScryfallImageSize = 'small' | 'normal' | 'large' | 'png' | 'art_crop' | 'border_crop';

const SIZE_PATTERN = /\/(small|normal|large|png|art_crop|border_crop)\//;

/**
 * Derive a different Scryfall image size URL from an existing one by swapping
 * the size path segment. Returns the original URL unchanged if the pattern
 * doesn't match (non-Scryfall URLs, unexpected format).
 */
export function deriveScryfallImageUrl(url: string, targetSize: ScryfallImageSize): string {
  return url.replace(SIZE_PATTERN, `/${targetSize}/`);
}

/**
 * Get the best source URL for a given target size from partial image URIs.
 * If the exact target size exists in `imageUris`, returns it directly.
 * Otherwise derives the URL from the best available key.
 */
export function getSourceUrl(imageUris: Partial<ImageUris> | null, targetSize: ScryfallImageSize): string | null {
  if (!imageUris) return null;

  // Exact match
  if (imageUris[targetSize]) return imageUris[targetSize]!;

  // Derive from best available (prefer larger sources to avoid upscaling)
  const fallbackOrder: ScryfallImageSize[] = ['large', 'normal', 'small'];
  for (const key of fallbackOrder) {
    if (imageUris[key]) {
      return deriveScryfallImageUrl(imageUris[key]!, targetSize);
    }
  }

  return null;
}

/** Layouts where each face has its own image_uris (dual-faced) */
export const DUAL_FACE_LAYOUTS: CardLayout[] = ['transform', 'modal_dfc', 'meld', 'reversible_card', 'double_faced_token'];

/** Check if a card has dual-face images that can be shown side-by-side */
export function hasDualFaceImages(layout: CardLayout | null | undefined, cardFaces: CardFace[] | null | undefined): boolean {
  if (!layout || !cardFaces || cardFaces.length < 2) return false;
  if (!DUAL_FACE_LAYOUTS.includes(layout)) return false;
  return cardFaces.every((face) => face.image_uris != null);
}
